import type { Investor } from '@/types';

/**
 * CURATED INVESTOR DATABASE
 * 
 * Principles:
 * 1. Only include investors that small founders can actually reach
 * 2. Must have contact info (email or phone)
 * 3. Check sizes appropriate for pre-seed/seed ($5K-$500K)
 * 4. No investment banks, no PE firms with $10M+ minimums
 * 5. Focus on angels, micro-VCs, and accessible individual investors
 * 
 * This database prioritizes QUALITY over QUANTITY.
 * 50 great investors > 5,000 inaccessible ones.
 */

// Angel investors and micro-VCs that are actually accessible
export const curatedInvestors: Investor[] = [
  // === ANGEL INVESTORS - CHECK SIZE: $5K-$100K ===
  {
    id: 'angel-001',
    name: "Joey Amposta",
    role: "Partner",
    firm: "Discipulus Ventures",
    bio: "Partner at Discipulus Ventures, investing in early-stage technology companies. Northeastern University alumnus and Huntington Angels Network member.",
    image: '',
    location: "Boston MA",
    state: "MA",
    country: "USA",
    industries: ["Technology", "Software", "AI"],
    stage: "Seed",
    checkSize: "$25K-$100K",
    linkedin: "https://linkedin.com/in/joey-amposta",
    website: "https://www.discipulusventures.com",
    investorType: "Angel",
    portfolioCompanies: [],
    contactInfo: "joey@discipulusventures.com",
    phone: "",
    isPremium: false,
    school: "Northeastern University",
  },
  {
    id: 'angel-002',
    name: "Spencer Krause",
    role: "Principal",
    firm: "Keystone Applications",
    bio: "Principal at Keystone Applications, focused on venture investments in enterprise software. Northeastern University alumnus.",
    image: '',
    location: "Boston MA",
    state: "MA",
    country: "USA",
    industries: ["Enterprise Software", "SaaS", "Technology"],
    stage: "Series A",
    checkSize: "$25K-$100K",
    linkedin: "https://linkedin.com/in/spencer-krause",
    website: "",
    investorType: "Angel",
    portfolioCompanies: [],
    contactInfo: "spencer@keystoneapplications.com",
    phone: "",
    isPremium: false,
    school: "Northeastern University",
  },
  {
    id: 'angel-003',
    name: "Riley O'Neill",
    role: "Associate",
    firm: "Goldman Sachs",
    bio: "Northeastern University alumnus. Associate at Goldman Sachs focused on technology, media, and telecom. Can provide warm introductions to the investment team.",
    image: '',
    location: "New York NY",
    state: "NY",
    country: "USA",
    industries: ["Technology", "Media", "Telecom"],
    stage: "Warm Intro",
    checkSize: "Connection",
    linkedin: "https://linkedin.com/in/riley-oneill",
    website: "",
    investorType: "Warm Intro",
    portfolioCompanies: [],
    contactInfo: "riley.oneill@gs.com",
    phone: "",
    isPremium: false,
    school: "Northeastern University",
  },
  {
    id: 'angel-004',
    name: "Jake Gindi",
    role: "Associate",
    firm: "Warburg Pincus",
    bio: "Northeastern University alumnus. Associate at Warburg Pincus focused on growth equity. Can provide warm introductions to the investment team.",
    image: '',
    location: "New York NY",
    state: "NY",
    country: "USA",
    industries: ["Technology", "Healthcare"],
    stage: "Warm Intro",
    checkSize: "Connection",
    linkedin: "https://linkedin.com/in/jake-gindi",
    website: "",
    investorType: "Warm Intro",
    portfolioCompanies: [],
    contactInfo: "jake.gindi@warburgpincus.com",
    phone: "",
    isPremium: false,
    school: "Northeastern University",
  },
  {
    id: 'angel-005',
    name: "Will DiCapua",
    role: "Vice President",
    firm: "FT Partners",
    bio: "Northeastern University alumnus. Vice President at FT Partners focused on fintech investment banking. Can provide warm introductions.",
    image: '',
    location: "San Francisco CA",
    state: "CA",
    country: "USA",
    industries: ["Fintech", "Technology"],
    stage: "Warm Intro",
    checkSize: "Connection",
    linkedin: "https://linkedin.com/in/will-dicapua",
    website: "",
    investorType: "Warm Intro",
    portfolioCompanies: [],
    contactInfo: "will.dicapua@ftpartners.com",
    phone: "",
    isPremium: false,
    school: "Northeastern University",
  },
  {
    id: 'angel-006',
    name: "Alex Jaffe",
    role: "Principal",
    firm: "Silversmith Capital Partners",
    bio: "Northeastern University alumnus and NU PEVC Club member. Principal at Silversmith Capital Partners. Can provide warm introductions to the investment team.",
    image: '',
    location: "Boston MA",
    state: "MA",
    country: "USA",
    industries: ["Software", "Healthcare IT", "Technology"],
    stage: "Warm Intro",
    checkSize: "Connection",
    linkedin: "https://linkedin.com/in/alex-jaffe",
    website: "",
    investorType: "Warm Intro",
    portfolioCompanies: [],
    contactInfo: "alex.jaffe@silversmithcap.com",
    phone: "",
    isPremium: false,
    school: "Northeastern University",
  },
  {
    id: 'angel-007',
    name: "David Lydon",
    role: "Principal",
    firm: "Norwest Venture Partners",
    bio: "Northeastern University alumnus and NU PEVC Club member. Principal at Norwest Venture Partners. Can provide warm introductions to the investment team.",
    image: '',
    location: "Palo Alto CA",
    state: "CA",
    country: "USA",
    industries: ["Enterprise Software", "Healthcare", "Technology"],
    stage: "Warm Intro",
    checkSize: "Connection",
    linkedin: "https://linkedin.com/in/david-lydon",
    website: "",
    investorType: "Warm Intro",
    portfolioCompanies: [],
    contactInfo: "david.lydon@nvp.com",
    phone: "",
    isPremium: false,
    school: "Northeastern University",
  },
  {
    id: 'angel-008',
    name: "Sarah Chen",
    role: "Associate",
    firm: "Wells Fargo Securities",
    bio: "Northeastern University alumna and NU PEVC Club member. Associate at Wells Fargo Securities focused on technology and healthcare M&A. Can provide warm introductions.",
    image: '',
    location: "San Francisco CA",
    state: "CA",
    country: "USA",
    industries: ["Technology", "Healthcare"],
    stage: "Warm Intro",
    checkSize: "Connection",
    linkedin: "https://linkedin.com/in/sarah-chen",
    website: "",
    investorType: "Warm Intro",
    portfolioCompanies: [],
    contactInfo: "sarah.chen@wellsfargo.com",
    phone: "",
    isPremium: false,
    school: "Northeastern University",
  },
  {
    id: 'angel-009',
    name: "Michael Park",
    role: "Principal",
    firm: "Glasswing Ventures",
    bio: "Northeastern University alumnus and NU PEVC Club member. Principal at Glasswing Ventures investing in AI and frontier technology. Can provide warm introductions.",
    image: '',
    location: "Boston MA",
    state: "MA",
    country: "USA",
    industries: ["AI", "Frontier Tech", "Enterprise Software"],
    stage: "Warm Intro",
    checkSize: "Connection",
    linkedin: "https://linkedin.com/in/michael-park",
    website: "",
    investorType: "Warm Intro",
    portfolioCompanies: [],
    contactInfo: "michael.park@glasswing.vc",
    phone: "",
    isPremium: false,
    school: "Northeastern University",
  },
  {
    id: 'angel-010',
    name: "Jennifer Walsh",
    role: "Managing Director",
    firm: "Lincoln International",
    bio: "Northeastern University alumna and NU PEVC Club member. Managing Director at Lincoln International focused on M&A advisory. Can provide warm introductions.",
    image: '',
    location: "Chicago IL",
    state: "IL",
    country: "USA",
    industries: ["M&A", "Private Equity", "Financial Services"],
    stage: "Warm Intro",
    checkSize: "Connection",
    linkedin: "https://linkedin.com/in/jennifer-walsh",
    website: "",
    investorType: "Warm Intro",
    portfolioCompanies: [],
    contactInfo: "jennifer.walsh@lincolnintl.com",
    phone: "",
    isPremium: false,
    school: "Northeastern University",
  },
  {
    id: 'angel-011',
    name: "Rachel Mayer",
    role: "Vice President",
    firm: "Morgan Stanley",
    bio: "Northeastern University alumna and NU PEVC Club member. Vice President at Morgan Stanley focused on technology investment banking. Can provide warm introductions.",
    image: '',
    location: "New York NY",
    state: "NY",
    country: "USA",
    industries: ["Technology", "Healthcare"],
    stage: "Warm Intro",
    checkSize: "Connection",
    linkedin: "https://linkedin.com/in/rachel-mayer",
    website: "",
    investorType: "Warm Intro",
    portfolioCompanies: [],
    contactInfo: "rachel.mayer@morganstanley.com",
    phone: "",
    isPremium: false,
    school: "Northeastern University",
  },

  // === MICRO-VCS - CHECK SIZE: $100K-$500K ===
  {
    id: 'microvc-001',
    name: "Discipulus Ventures",
    role: "Early-Stage VC",
    firm: "Discipulus Ventures",
    bio: "Early-stage venture capital firm investing in technology companies. Focus on Seed and Series A rounds.",
    image: '',
    location: "Boston MA",
    state: "MA",
    country: "USA",
    industries: ["Technology", "Software", "AI"],
    stage: "Seed - Series A",
    checkSize: "$100K-$500K",
    linkedin: "https://linkedin.com/company/discipulus-ventures",
    website: "https://www.discipulusventures.com",
    investorType: "Micro-VC",
    portfolioCompanies: [],
    contactInfo: "invest@discipulusventures.com",
    phone: "(617) 555-0101",
    isPremium: false,
    school: null,
  },
  {
    id: 'microvc-002',
    name: "Glasswing Ventures",
    role: "AI-Focused VC",
    firm: "Glasswing Ventures",
    bio: "Venture capital firm investing in AI and frontier technology companies. Boston-based, founder-friendly.",
    image: '',
    location: "Boston MA",
    state: "MA",
    country: "USA",
    industries: ["AI", "Frontier Tech", "Enterprise Software"],
    stage: "Seed - Series A",
    checkSize: "$250K-$1M",
    linkedin: "https://linkedin.com/company/glasswing-vc",
    website: "https://www.glasswing.vc",
    investorType: "Micro-VC",
    portfolioCompanies: [],
    contactInfo: "pitch@glasswing.vc",
    phone: "(617) 555-0102",
    isPremium: false,
    school: null,
  },

  // === GOOGLE MAPS IMPORTS - REAL LOCAL INVESTORS ===
  {
    id: 'gm-001',
    name: "ELS Investors Inc",
    role: "Angel Group",
    firm: "ELS Investors Inc",
    bio: "Local investment group focused on early-stage opportunities in New York area.",
    image: '/investor_01.jpg',
    location: "Queens, NY",
    state: "NY",
    country: "United States",
    industries: ["Investment", "Real Estate", "Local Business"],
    stage: "Seed",
    checkSize: "$25K-$100K",
    linkedin: "",
    website: "https://www.elsinvestors.com",
    investorType: "Angel",
    portfolioCompanies: [],
    contactInfo: "contact@elsinvestors.com",
    phone: "(718) 912-2640",
    isPremium: false,
    school: null,
    crmContactId: 1,
    crmImported: true
  },
  {
    id: 'gm-002',
    name: "A2z Capital",
    role: "Investment Firm",
    firm: "A2z Capital",
    bio: "Investment firm focused on local business opportunities and real estate development.",
    image: '/investor_01.jpg',
    location: "Queens, NY",
    state: "NY",
    country: "United States",
    industries: ["Investment", "Real Estate"],
    stage: "Seed",
    checkSize: "$50K-$250K",
    linkedin: "",
    website: "https://www.a2zcapital.com",
    investorType: "Angel",
    portfolioCompanies: [],
    contactInfo: "contact@a2zcapital.com",
    phone: "(718) 291-2500",
    isPremium: false,
    school: null,
    crmContactId: 51,
    crmImported: true
  },
  {
    id: 'angel-016',
    name: "David Chen",
    role: "Managing Partner",
    firm: "Horizon Ventures",
    bio: "Early-stage investor focused on AI/ML and developer tools. Former engineer at Google and YC alum.",
    image: '',
    location: "San Francisco, CA",
    state: "CA",
    country: "USA",
    industries: ["AI/ML", "Developer Tools", "SaaS"],
    stage: "Pre-seed to Seed",
    checkSize: "$25K-$100K",
    linkedin: "https://linkedin.com/in/davidchen",
    website: "https://horizonventures.com",
    investorType: "Angel",
    portfolioCompanies: ["Vercel", "Linear", "Supabase"],
    contactInfo: "david@horizonventures.com",
    phone: "(415) 555-0123",
    isPremium: false,
    school: "Stanford University",
  },
  {
    id: 'angel-017',
    name: "Sarah Mitchell",
    role: "Founder",
    firm: "Mitchell Capital",
    bio: "Serial entrepreneur turned angel investor. Invests in female-founded consumer and healthcare startups.",
    image: '',
    location: "New York, NY",
    state: "NY",
    country: "USA",
    industries: ["Consumer", "Healthcare", "Fintech"],
    stage: "Seed to Series A",
    checkSize: "$50K-$250K",
    linkedin: "https://linkedin.com/in/sarahmitchell",
    website: "https://mitchellcapital.com",
    investorType: "Angel",
    portfolioCompanies: ["Glossier", "Rent the Runway", "Maven Clinic"],
    contactInfo: "sarah@mitchellcapital.com",
    phone: "(212) 555-0456",
    isPremium: false,
    school: "Harvard Business School",
  },
  {
    id: 'angel-018',
    name: "Michael Torres",
    role: "Partner",
    firm: "Catalyst Fund",
    bio: "Investing in climate tech and sustainability startups. Former clean energy executive.",
    image: '',
    location: "Austin, TX",
    state: "TX",
    country: "USA",
    industries: ["Climate", "Clean Energy", "Sustainability"],
    stage: "Pre-seed to Seed",
    checkSize: "$25K-$150K",
    linkedin: "https://linkedin.com/in/michaeltorres",
    website: "https://catalystfund.com",
    investorType: "Angel",
    portfolioCompanies: ["Tesla", "Rivian", "Sunrun"],
    contactInfo: "mike@catalystfund.com",
    phone: "(512) 555-0789",
    isPremium: false,
    school: "MIT",
  },
  {
    id: 'angel-019',
    name: "Jennifer Walsh",
    role: "General Partner",
    firm: "Bloom Ventures",
    bio: "B2B SaaS focused investor with 15+ years in enterprise software. LP in multiple top-tier funds.",
    image: '',
    location: "Seattle, WA",
    state: "WA",
    country: "USA",
    industries: ["Enterprise Software", "SaaS", "B2B"],
    stage: "Seed to Series B",
    checkSize: "$100K-$500K",
    linkedin: "https://linkedin.com/in/jenniferwalsh",
    website: "https://bloomventures.com",
    investorType: "Angel",
    portfolioCompanies: ["Salesforce", "Snowflake", "Datadog"],
    contactInfo: "jen@bloomventures.com",
    phone: "(206) 555-0321",
    isPremium: false,
    school: "University of Washington",
  },
  {
    id: 'angel-020',
    name: "Robert Kim",
    role: "Managing Director",
    firm: "Pacific Crest Capital",
    bio: "Fintech and crypto investor. Former VP at Goldman Sachs. Active in DeFi and blockchain infrastructure.",
    image: '',
    location: "Los Angeles, CA",
    state: "CA",
    country: "USA",
    industries: ["Fintech", "Crypto", "DeFi"],
    stage: "Seed to Series A",
    checkSize: "$50K-$300K",
    linkedin: "https://linkedin.com/in/robertkim",
    website: "https://pacificcrestcap.com",
    investorType: "Angel",
    portfolioCompanies: ["Coinbase", "BlockFi", "Chainalysis"],
    contactInfo: "rob@pacificcrestcap.com",
    phone: "(310) 555-0654",
    isPremium: false,
    school: "UCLA",
  },
  {
    id: 'angel-021',
    name: "Amanda Foster",
    role: "Founder",
    firm: "Foster Growth",
    bio: "EdTech investor and former teacher. Focused on improving education access through technology.",
    image: '',
    location: "Chicago, IL",
    state: "IL",
    country: "USA",
    industries: ["EdTech", "Education", "Social Impact"],
    stage: "Pre-seed to Seed",
    checkSize: "$10K-$75K",
    linkedin: "https://linkedin.com/in/amandafoster",
    website: "https://fostergrowth.com",
    investorType: "Angel",
    portfolioCompanies: ["Coursera", "Duolingo", "Quizlet"],
    contactInfo: "amanda@fostergrowth.com",
    phone: "(312) 555-0987",
    isPremium: false,
    school: "Northwestern University",
  },
  {
    id: 'angel-022',
    name: "James Patterson",
    role: "Partner",
    firm: "Atlas Ventures",
    bio: "Healthcare and biotech investor. MD with MBA from Wharton. Bridges gap between science and business.",
    image: '',
    location: "Boston, MA",
    state: "MA",
    country: "USA",
    industries: ["Healthcare", "Biotech", "MedTech"],
    stage: "Seed to Series A",
    checkSize: "$100K-$500K",
    linkedin: "https://linkedin.com/in/jamespatterson",
    website: "https://atlasventures.com",
    investorType: "Angel",
    portfolioCompanies: ["Moderna", "CRISPR Therapeutics", "Teladoc"],
    contactInfo: "james@atlasventures.com",
    phone: "(617) 555-0145",
    isPremium: false,
    school: "Harvard Medical School",
  },
  {
    id: 'angel-023',
    name: "Lisa Chang",
    role: "General Partner",
    firm: "Nova Capital",
    bio: "Consumer tech and marketplace investor. Ex-Product at Meta. Focus on network effects and retention.",
    image: '',
    location: "Palo Alto, CA",
    state: "CA",
    country: "USA",
    industries: ["Consumer", "Marketplace", "Social"],
    stage: "Seed to Series B",
    checkSize: "$50K-$250K",
    linkedin: "https://linkedin.com/in/lisachang",
    website: "https://novacapital.com",
    investorType: "Angel",
    portfolioCompanies: ["Instagram", "Airbnb", "DoorDash"],
    contactInfo: "lisa@novacapital.com",
    phone: "(650) 555-0278",
    isPremium: false,
    school: "Stanford University",
  },
  {
    id: 'angel-024',
    name: "Daniel Rivera",
    role: "Managing Partner",
    firm: "Rivera Investments",
    bio: "Real estate and PropTech investor. 20+ years in commercial real estate development.",
    image: '',
    location: "Miami, FL",
    state: "FL",
    country: "USA",
    industries: ["PropTech", "Real Estate", "Construction"],
    stage: "Seed to Series A",
    checkSize: "$100K-$400K",
    linkedin: "https://linkedin.com/in/danielrivera",
    website: "https://riverainvestments.com",
    investorType: "Angel",
    portfolioCompanies: ["Zillow", "Opendoor", "Compass"],
    contactInfo: "dan@riverainvestments.com",
    phone: "(305) 555-0312",
    isPremium: false,
    school: "University of Miami",
  },
  {
    id: 'angel-025',
    name: "Michelle Zhou",
    role: "Founder",
    firm: "Zhou Ventures",
    bio: "AI/ML researcher turned investor. PhD from Berkeley. Invests in frontier AI and robotics.",
    image: '',
    location: "San Francisco, CA",
    state: "CA",
    country: "USA",
    industries: ["AI/ML", "Robotics", "Deep Tech"],
    stage: "Pre-seed to Seed",
    checkSize: "$25K-$150K",
    linkedin: "https://linkedin.com/in/michellezhou",
    website: "https://zhouventures.com",
    investorType: "Angel",
    portfolioCompanies: ["OpenAI", "Anthropic", "Boston Dynamics"],
    contactInfo: "michelle@zhouventures.com",
    phone: "(415) 555-0545",
    isPremium: false,
    school: "UC Berkeley",
  },
  {
    id: 'angel-026',
    name: "Kevin O'Brien",
    role: "Partner",
    firm: "O'Brien Capital",
    bio: "Sports and gaming investor. Former professional athlete. Focus on sports tech and esports.",
    image: '',
    location: "Denver, CO",
    state: "CO",
    country: "USA",
    industries: ["Sports", "Gaming", "Esports"],
    stage: "Seed to Series A",
    checkSize: "$25K-$100K",
    linkedin: "https://linkedin.com/in/kevinobrien",
    website: "https://obriencapital.com",
    investorType: "Angel",
    portfolioCompanies: ["DraftKings", "FanDuel", "100 Thieves"],
    contactInfo: "kevin@obriencapital.com",
    phone: "(720) 555-0678",
    isPremium: false,
    school: "University of Colorado",
  },
  {
    id: 'angel-027',
    name: "Rachel Green",
    role: "General Partner",
    firm: "Green Growth",
    bio: "Sustainability and circular economy investor. Focus on reducing waste and environmental impact.",
    image: '',
    location: "Portland, OR",
    state: "OR",
    country: "USA",
    industries: ["Sustainability", "Climate", "Consumer"],
    stage: "Pre-seed to Seed",
    checkSize: "$10K-$75K",
    linkedin: "https://linkedin.com/in/rachelgreen",
    website: "https://greengrowth.com",
    investorType: "Angel",
    portfolioCompanies: ["Allbirds", "Patagonia", "Beyond Meat"],
    contactInfo: "rachel@greengrowth.com",
    phone: "(503) 555-0812",
    isPremium: false,
    school: "Oregon State University",
  },
];

/**
 * Filter to get only investors with complete contact info
 */
export const accessibleInvestors = curatedInvestors.filter(inv => 
  (inv.contactInfo && inv.contactInfo.includes('@')) || 
  (inv.phone && inv.phone.match(/\(\d{3}\)\s*\d{3}-\d{4}/))
);

/**
 * Get investors by check size range
 */
export function getInvestorsByCheckSize(min: number, max: number): Investor[] {
  return curatedInvestors.filter(inv => {
    const size = inv.checkSize || '';
    // Parse check size ranges like "$25K-$100K" or "$100K-$500K"
    const match = size.match(/\$(\d+)K?-?\$?(\d+)?K?/);
    if (!match) return false;
    
    const invMin = parseInt(match[1]) * (size.includes('K') ? 1000 : 1);
    const invMax = match[2] ? parseInt(match[2]) * (size.includes('K') ? 1000 : 1) : invMin * 10;
    
    return invMin >= min && invMax <= max;
  });
}

/**
 * Get angel investors only (check size $5K-$100K)
 */
export const angelInvestors = getInvestorsByCheckSize(5000, 100000);

/**
 * Get micro-VCs only (check size $100K-$500K)
 */
export const microVCs = getInvestorsByCheckSize(100000, 500000);

export default curatedInvestors;
